import jsPDF from 'jspdf';
import { Event } from '@/models/event';
import { Firestore } from 'firebase/firestore';
import { BRAND_COLOR, FONT_HELCH, PaperSize, PAPER_DIMENSIONS } from '../pdf-constants';
import { drawImageCover, drawImageContain, fetchMonthImages, fetchClubInfo } from '../pdf-utils';
import { toAdHocPacificDate, getPacificMonthKey } from '../../date-utils';

export async function exportMonthlyCalendarFormat(
    events: Event[],
    firestore: Firestore,
    paperSize: PaperSize = 'letter',
    images?: Record<string, string>,
    year?: number
) {
    // 1. Dims
    const dim = PAPER_DIMENSIONS[paperSize];
    // Monthly Wall is typically Landscape if it's single page or "opened" view?
    // Actually, if it's "Wall Folding", it's usually two Portrait pages (Top/Bottom) that hang.
    // But `monthly-exporter` was set to 'landscape'.
    // If we want it to match "Wall Folding" layout (which is 11x17 Portrait folded to 11x8.5 Landscape Face),
    // Then "Monthly Wall" here should probably be a Landscape page (e.g. 11x8.5) representing one "Face".
    // So we generate sequential Landscape pages.

    const isLandscape = true;
    const doc = new jsPDF({
        orientation: isLandscape ? 'landscape' : 'portrait',
        unit: 'mm',
        format: paperSize
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Scale Logic (Baseline: 11in width for letter landscape?)
    // 11 inches = 279.4 mm.
    // scale = pageWidth / 11 (if in inches).
    // Let's use mm math. 11 inches is approx 280mm.
    const scale = pageWidth / 279.4; // Normalized to ~Letter Landscape width

    // Group Events
    const eventsByMonth: Record<string, Event[]> = {};
    const years = new Set<string>();

    events.forEach(e => {
        const key = getPacificMonthKey(e.date);
        const eventYear = key.split('-')[0];
        if (year && parseInt(eventYear) !== year) return;
        years.add(eventYear);
        if (!eventsByMonth[key]) eventsByMonth[key] = [];
        eventsByMonth[key].push(e);
    });

    // Ensure full year
    const targetYears = year ? [String(year)] : Array.from(years);
    targetYears.forEach(y => {
        for (let m = 1; m <= 12; m++) {
            const key = `${y}-${String(m).padStart(2, '0')}`;
            if (!eventsByMonth[key]) eventsByMonth[key] = [];
        }
    });

    const sortedMonths = Object.keys(eventsByMonth).sort();

    // Determine Start (First available or Jan of current/selected year)
    let startY = new Date().getFullYear();
    if (year) startY = year;
    else if (sortedMonths.length > 0) startY = parseInt(sortedMonths[0].split('-')[0]);

    // Generate 12 Consecutive Month Keys for that year
    const targetMonthKeys: string[] = [];
    for (let m = 1; m <= 12; m++) {
        targetMonthKeys.push(`${startY}-${String(m).padStart(2, '0')}`);
    }

    // Fetch Assets
    const [mediaMap, clubInfo] = await Promise.all([
        images ? Promise.resolve(images) : fetchMonthImages(firestore, targetMonthKeys),
        fetchClubInfo(firestore)
    ]);

    // RENDER HELPERS (Duplicated logic from Saddle Stitch for consistency)
    const renderCover = async () => {
        doc.setFillColor(255, 255, 255);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');

        // Logo
        if (clubInfo?.clubLogoUrl) {
            try {
                const logoMargin = 25 * scale;
                // Center logo in upper area
                await drawImageContain(doc, clubInfo.clubLogoUrl, pageWidth / 2 - (pageWidth / 4), logoMargin, pageWidth / 2, pageHeight / 2);
            } catch (e) { }
        } else {
            doc.setTextColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
            doc.setFontSize(60 * scale);
            doc.setFont(FONT_HELCH, 'bold');
            doc.text("SRAC", pageWidth / 2, pageHeight / 2 - (10 * scale), { align: 'center' });
        }

        doc.setTextColor(0, 0, 0);
        doc.setFontSize(24 * scale);
        doc.setFont(FONT_HELCH, 'bold');
        doc.text(`${startY} Calendar`, pageWidth / 2, pageHeight / 2 + (40 * scale), { align: 'center' });

        // Red Bar
        const barH = 50 * scale; // mm
        doc.setFillColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
        doc.rect(0, pageHeight - barH, pageWidth, barH, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18 * scale);
        doc.setFont(FONT_HELCH, 'bold');

        if (clubInfo) {
            let yPos = pageHeight - barH + (20 * scale);
            if (clubInfo.clubWebsite) {
                doc.text(clubInfo.clubWebsite.replace(/^https?:\/\//, ''), pageWidth / 2, yPos, { align: 'center' });
                yPos += (10 * scale);
            }
            if (clubInfo.clubEmail) {
                doc.setFontSize(14 * scale);
                doc.setFont(FONT_HELCH, 'normal');
                doc.text(clubInfo.clubEmail, pageWidth / 2, yPos, { align: 'center' });
            }
        }
    };

    const renderMonthImage = async (key: string, name: string, yStr: string) => {
        const imageUrl = mediaMap[key];
        if (imageUrl) {
            await drawImageCover(doc, imageUrl, 0, 0, pageWidth, pageHeight, 'mm');
            // Overlay
            const bannerH = 40 * scale; // mm
            doc.setFillColor(0, 0, 0);
            doc.setGState(new (doc as any).GState({ opacity: 0.4 }));
            doc.rect(0, pageHeight - bannerH, pageWidth, bannerH, 'F');
            doc.setGState(new (doc as any).GState({ opacity: 1 }));

            doc.setTextColor(255, 255, 255);
            doc.setFontSize(40 * scale);
            doc.setFont(FONT_HELCH, 'bold');
            doc.text(name, 15 * scale, pageHeight - (12 * scale));

            doc.setFontSize(20 * scale);
            doc.text(yStr, pageWidth - (15 * scale), pageHeight - (12 * scale), { align: 'right' });
        } else {
            // Fallback
            doc.setFillColor(240, 240, 240);
            doc.rect(0, 0, pageWidth, pageHeight, 'F');
            doc.setTextColor(150, 150, 150);
            doc.setFontSize(24);
            doc.text(name, pageWidth / 2, pageHeight / 2, { align: 'center' });
        }
    };

    const renderMonthGrid = async (key: string, name: string, yStr: string, evs: Event[]) => {
        const padding = 12 * scale; // mm
        const footerH = 20 * scale;

        const contentX = padding;
        const contentY = padding;
        const contentW = pageWidth - (padding * 2);
        const contentH = pageHeight - (padding * 2) - footerH;

        // Header
        doc.setTextColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
        doc.setFontSize(14 * scale);
        doc.setFont(FONT_HELCH, 'bold');
        doc.text(`${name} ${yStr}`, contentX, contentY + (5 * scale));

        // Days Header
        const daysY = contentY + (10 * scale);
        const headH = 8 * scale;
        doc.setFillColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
        doc.rect(contentX, daysY, contentW, headH, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10 * scale);
        doc.setFont(FONT_HELCH, 'bold');

        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const colW = contentW / 7;
        days.forEach((d, i) => {
            doc.text(d, contentX + i * colW + colW / 2, daysY + (5 * scale), { align: 'center' });
        });

        // Grid
        const gridY = daysY + headH;

        const [yy, mm] = key.split('-').map(Number);
        const firstDay = new Date(yy, mm - 1, 1).getDay();
        const daysInMonth = new Date(yy, mm, 0).getDate();
        const numRows = Math.ceil((firstDay + daysInMonth) / 7);

        // Height Allocation (Similar to Saddle Stitch logic)
        const gridH = contentH * 0.70;
        const rowH = gridH / numRows;

        let curDate = 1;
        doc.setLineWidth(0.1);
        doc.setDrawColor(200, 200, 200);

        for (let r = 0; r < numRows; r++) {
            for (let c = 0; c < 7; c++) {
                const cellX = contentX + c * colW;
                const cellY = gridY + r * rowH;

                let isValidDate = true;
                if (r === 0 && c < firstDay) isValidDate = false;
                else if (curDate > daysInMonth) isValidDate = false;

                if (!isValidDate) {
                    doc.setFillColor(245, 245, 245);
                    doc.rect(cellX, cellY, colW, rowH, 'FD');
                } else {
                    doc.rect(cellX, cellY, colW, rowH); // Draw border

                    // Date Number
                    doc.setTextColor(0, 0, 0);
                    doc.setFontSize(10 * scale);
                    doc.setFont(FONT_HELCH, 'bold');
                    doc.text(String(curDate), cellX + colW - (1 * scale), cellY + (4 * scale), { align: 'right' });

                    // Dots
                    const dayEvs = evs.filter(e => toAdHocPacificDate(e.date).getUTCDate() === curDate);
                    if (dayEvs.length > 0) {
                        let ey = cellY + (8 * scale);
                        doc.setFontSize(7 * scale);
                        doc.setFont(FONT_HELCH, 'normal');
                        doc.setTextColor(50, 50, 50);

                        dayEvs.slice(0, 3).forEach(ev => {
                            const title = ev.title.length > 20 ? ev.title.substring(0, 18) + '..' : ev.title;
                            doc.text(title, cellX + (1 * scale), ey);
                            ey += (3 * scale);
                        });
                    }
                    curDate++;
                }
            }
        }

        // 4. Details List
        const listY = gridY + (numRows * rowH) + (5 * scale);
        const listBottom = pageHeight - padding - footerH;
        const listDetailH = listBottom - listY - (5 * scale);

        doc.setFontSize(12 * scale);
        doc.setTextColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
        doc.setFont(FONT_HELCH, 'bold');
        doc.text("Events & Activities", contentX, listY);

        // Deduplicate and Sort
        evs.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        const uniqueEvents = Array.from(new Set(evs.map(e => e.id))).map(id => evs.find(e => e.id === id)!);

        if (uniqueEvents.length > 0) {
            // Adaptive Columns Strategy (mm based)
            const itemH = 8 * scale;
            const strategies = [
                { cols: 2, fontScale: 1 },
                { cols: 3, fontScale: 0.9 },
                { cols: 4, fontScale: 0.8 }
            ];

            let selectedStrat = strategies[0];
            for (const strat of strategies) {
                const capacity = Math.floor(listDetailH / itemH) * strat.cols;
                if (uniqueEvents.length <= capacity) {
                    selectedStrat = strat;
                    break;
                }
                selectedStrat = strat;
            }

            const { cols, fontScale } = selectedStrat;
            let colIndex = 0;
            let colBaseX = contentX + (2 * scale);
            let curY = listY + (8 * scale);
            const startY = curY;

            for (const ev of uniqueEvents) {
                if (curY + itemH > listBottom) {
                    colIndex++;
                    if (colIndex >= cols) break;
                    curY = startY;
                    colBaseX = contentX + (2 * scale) + (colIndex * (contentW / cols));
                }

                const d = toAdHocPacificDate(ev.date);
                const localDate = d.toLocaleDateString('en-US', { day: 'numeric', weekday: 'short', timeZone: 'UTC' });
                const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });

                doc.setFontSize(9 * scale * fontScale);
                doc.setTextColor(0, 0, 0);
                doc.setFont(FONT_HELCH, 'bold');
                doc.text(`${localDate} - ${ev.title}`, colBaseX, curY);

                doc.setFontSize(8 * scale * fontScale);
                doc.setTextColor(100, 100, 100);
                doc.setFont(FONT_HELCH, 'normal');
                doc.text(`${time} @ ${ev.location || 'Field'}`, colBaseX, curY + (3 * scale));

                curY += itemH;
            }
        }

        // Footer
        const footerY = pageHeight - padding - (footerH / 2);
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.1);
        doc.line(contentX, pageHeight - padding - footerH, contentX + contentW, pageHeight - padding - footerH);

        if (clubInfo?.clubLogoUrl) {
            try {
                const fLogoH = 12 * scale;
                await drawImageContain(doc, clubInfo.clubLogoUrl, contentX, pageHeight - padding - footerH + (4 * scale), fLogoH, fLogoH);
                doc.setFontSize(8 * scale);
                doc.setTextColor(100, 100, 100);
                doc.setFont(FONT_HELCH, 'bold');
                doc.text(clubInfo.clubName || 'SRAC', contentX + fLogoH + (5 * scale), footerY + (2 * scale));
            } catch (e) { }
        }

        if (clubInfo?.clubWebsite) {
            doc.setFontSize(8 * scale);
            doc.setTextColor(100, 100, 100);
            const userUrl = clubInfo.clubWebsite.replace(/^https?:\/\//, '');
            doc.text(userUrl, contentX + contentW, footerY + (2 * scale), { align: 'right' });
        }
    };

    // GENERATE PAGES
    // 1. Cover
    await renderCover();

    // 2. Months
    for (const k of targetMonthKeys) {
        const [y, m] = k.split('-');
        const date = new Date(Date.UTC(Number(y), Number(m) - 1, 1));
        const mName = date.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });

        doc.addPage();
        await renderMonthImage(k, mName, y);

        doc.addPage();
        await renderMonthGrid(k, mName, y, eventsByMonth[k] || []);
    }

    // 3. Back
    // Reuse cover logic simplistically or make blank with logo? 
    doc.addPage();
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');
    if (clubInfo?.clubLogoUrl) {
        await drawImageContain(doc, clubInfo.clubLogoUrl, pageWidth / 2 - 20, pageHeight / 2 - 20, 40, 40);
    }
    doc.setTextColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
    doc.setFontSize(16 * scale);
    doc.setFont(FONT_HELCH, 'bold');
    doc.text("Snohomish Radio Aero Club", pageWidth / 2, pageHeight / 2 + 25, { align: 'center' });
    if (clubInfo?.clubWebsite) {
        doc.text(clubInfo.clubWebsite, pageWidth / 2, pageHeight / 2 + 35, { align: 'center' });
    }

    doc.save(`srac-monthly-calendar-${startY}.pdf`);
}
